import axios from "axios";

// Public YouTube channel feeds are served as RSS at
// https://www.youtube.com/feeds/videos.xml?channel_id=<ID>
// YouTube does not send CORS headers on that endpoint, so the browser cannot
// read it directly. rss2json proxies the feed and responds with
// Access-Control-Allow-Origin: *, which makes it usable from the client with
// no API key and no backend.
const RSS_TO_JSON = "https://api.rss2json.com/v1/api.json";

const decodeEntities = (value = "") =>
  value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");

// Feed links arrive as /watch?v=<id> or /shorts/<id>. Both embed via
// /embed/<id>, so we normalise to the bare id and build the player URL later.
const extractVideoId = (link = "") => {
  const watchMatch = link.match(/[?&]v=([^&]+)/);
  if (watchMatch) return watchMatch[1];
  const shortsMatch = link.match(/\/(?:shorts|embed|v)\/([^?/]+)/);
  return shortsMatch ? shortsMatch[1] : null;
};

const buildThumbnail = (item) => {
  if (!item.thumbnail) return null;
  return item.thumbnail.replace("/hqdefault.jpg", "/mqdefault.jpg");
};

export const fetchChannelFeed = async (channelId) => {
  if (!channelId) return [];

  const feedUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;

  try {
    const { data } = await axios.get(RSS_TO_JSON, {
      params: { rss_url: feedUrl },
      headers: { "Content-Type": "application/json" },
    });

    if (data.status !== "ok" || !Array.isArray(data.items)) return [];

    return data.items
      .map((item) => {
        const videoId = extractVideoId(item.link);
        if (!videoId) return null;

        return {
          videoId,
          title: decodeEntities(item.title || "Untitled video"),
          description: decodeEntities(item.description || ""),
          thumbnail: buildThumbnail(item),
          link: item.link,
          publishedAt: item.pubDate || null,
        };
      })
      .filter(Boolean);
  } catch (error) {
    // A dead channel should not take down the rest of the page, so callers get
    // an empty list and the UI renders the rest of its content.
    console.error(`Error fetching YouTube feed for ${channelId}:`, error.message);
    return [];
  }
};

export const fetchLatestVideo = async (channelId) => {
  const feed = await fetchChannelFeed(channelId);
  return feed.length ? feed[0] : null;
};